import { useEffect } from 'react'

/**
 * **Das Textfeld des Titels ist so hoch wie sein Text** — sofort, auch beim
 * Aufklappen.
 *
 * Hier hielt eine Fessel den Titel 280ms lang einzeilig, damit die Zeile nicht
 * springt, „bevor die Karte gewachsen ist"; danach fuhr seine Höhe nach. Das
 * war genau die falsche Reihenfolge (Nutzer, 21.09.2026): Die Karte wuchs auf
 * ihre Zielhöhe, stand — und wurde gleich darauf nochmal höher, weil der Titel
 * erst dann umbrach. Zweimal wachsen fällt mehr auf als einmal.
 *
 * Jetzt steht die Endhöhe von Anfang an, und die Hülle des Titels
 * (`AutoHeight` in `TaskEntry`) fährt sie GLEICHZEITIG mit dem aufgehenden
 * Rumpf — eine Bewegung.
 *
 * Ein `ResizeObserver` hält die Höhe bei Breitenänderungen nach (Fenster,
 * Navigation), gedrosselt über rAF.
 */
export function useAutosizeTitle(
  titleInputRef: React.RefObject<HTMLTextAreaElement | null>,
  isExpanded: boolean,
  title: string,
): void {
  // ── Auto-resize ──
  useEffect(() => {
    const el = titleInputRef.current
    if (!el) return

    let heightRafId: number | null = null
    const scheduleSimpleResize = () => {
      if (heightRafId !== null) return
      heightRafId = requestAnimationFrame(() => {
        heightRafId = null
        el.style.transition = ''
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    }

    // Die Höhe steht sofort — die Bewegung macht die Hülle darüber, nicht das
    // Feld selbst. Zwei Stellen, die dieselbe Höhe animieren, liefen gegeneinander.
    scheduleSimpleResize()

    // ResizeObserver: throttle via rAF so rapid width changes coalesce into one write
    let observerRafId: number | null = null
    let lastWidth = el.getBoundingClientRect().width
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width
      if (newWidth === lastWidth) return
      lastWidth = newWidth
      if (observerRafId !== null) return
      observerRafId = requestAnimationFrame(() => {
        observerRafId = null
        el.style.transition = ''
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    })
    observer.observe(el)

    return () => {
      if (heightRafId !== null) cancelAnimationFrame(heightRafId)
      if (observerRafId !== null) cancelAnimationFrame(observerRafId)
      observer.disconnect()
    }
  }, [title, isExpanded, titleInputRef])
}
